import React, { useRef, useState } from 'react';
import Icon from '../AppIcon';
import useTranslation from '../../hooks/useTranslation';

/**
 * DocumentAttach — zone de televersement + liste de documents.
 *
 * Extrait du motif de DocumentsModal, qui est le composant documentaire le plus
 * partage de l'app (monte par 6 pages). Meme apparence : zone pointillee,
 * glisser-deposer, icone Upload, liste avec icone par type et taille, et une
 * corbeille au survol.
 *
 * A la difference de DocumentsModal, ce composant ne connait PAS la source de
 * donnees : tout passe par des props. Il peut donc servir a n'importe quelle
 * entite (denonciations aujourd'hui, autre chose demain) sans dupliquer l'UX.
 *
 * Props:
 *   documents   [{ id, file_name, mime_type, size_bytes }]
 *   onOpen(doc) ouvre le document (URL signee cote appelant)
 *   onAdd(file)  televerse un fichier ; peut etre appele plusieurs fois
 *   onRemove(doc) demande la suppression (l'appelant confirme)
 *   uploading   booleen — desactive la zone et montre le spinner
 *   accept      attribut accept de l'input (defaut : PDF + JPEG + PNG)
 *   maxBytes    taille max, pour le message d'aide
 *   hint        texte d'aide sous la zone ; remplace le libelle par defaut
 *   disabled    desactive l'ajout (ex. entite pas encore enregistree)
 *   disabledHint message affiche a la place de la zone quand disabled
 *   onComplete() appele une fois la selection entierement traitee. C'est la
 *                que l'appelant doit rafraichir ses donnees : rafraichir a
 *                chaque fichier ferait clignoter toute la page.
 *   multiple    autorise la selection de plusieurs fichiers (defaut true)
 *   compact     variante dense, pour un usage en cellule de tableau
 *   showList    affiche la liste des documents (defaut true). false : seule la
 *               zone de depot ; `documents` sert encore a ecarter les doublons.
 *
 * Doublons : avant tout ajout, le nom du fichier est compare aux documents
 * deja presents, normalise (trim + minuscules + espaces reduits, extension
 * comprise). Un doublon n'est pas televerse et le nom est signale. Sur une
 * selection multiple, seuls les doublons sont ecartes ; le reste part.
 */

const ACCEPT_DEFAULT = 'application/pdf,image/jpeg,image/png';

const fileTypeIcon = (name, mime) => {
  const ext = (name || '')?.split('.')?.pop()?.toLowerCase();
  if (mime === 'application/pdf' || ext === 'pdf')
    return <Icon name="FileText" size={15} className="text-red-500 flex-shrink-0" />;
  if ((mime || '')?.startsWith('image/') || ['jpg', 'jpeg', 'png']?.includes(ext))
    return <Icon name="Image" size={15} className="text-purple-500 flex-shrink-0" />;
  return <Icon name="File" size={15} className="text-gray-400 flex-shrink-0" />;
};

// Comparaison de noms : trim, minuscules, espaces reduits, extension incluse.
const normalizeName = (name) =>
  (name || '')?.trim()?.toLowerCase()?.replace(/\s+/g, ' ');

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
};

const DocumentAttach = ({
  documents = [],
  onOpen,
  onAdd,
  onRemove,
  onComplete,
  uploading = false,
  accept = ACCEPT_DEFAULT,
  hint,
  disabled = false,
  disabledHint,
  multiple = true,
  compact = false,
  showList = true,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null); // { done, total }

  const pick = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files?.length === 0) return;
    setError('');

    // Noms deja presents, normalises une fois pour toute la selection.
    const taken = new Set(
      (documents || [])?.map((d) => normalizeName(d?.file_name))
    );

    const accepted = [];
    const skipped = [];
    for (const file of files) {
      const key = normalizeName(file?.name);
      // `taken` grandit au fil de la boucle : deux fichiers de meme nom dans
      // la meme selection ne passent pas deux fois.
      if (taken?.has(key)) skipped?.push(file?.name);
      else {
        taken?.add(key);
        accepted?.push(file);
      }
    }

    if (skipped?.length === 1) {
      setError(t('documents.duplicateName', { name: skipped?.[0] }));
    } else if (skipped?.length > 1) {
      setError(t('documents.duplicatesSkipped', { names: skipped?.join(', ') }));
    }

    if (accepted?.length === 0) return;

    // Sequentiel, avec progression. L'appelant ne rafraichit qu'a la fin,
    // via onComplete : un rafraichissement par fichier ferait clignoter la page.
    setProgress({ done: 0, total: accepted?.length });
    try {
      for (let i = 0; i < accepted?.length; i += 1) {
        await onAdd?.(accepted?.[i]);
        setProgress({ done: i + 1, total: accepted?.length });
      }
    } finally {
      setProgress(null);
      await onComplete?.();
    }
  };

  const busy = uploading || !!progress;

  return (
    <div className="space-y-2">
      {/* Liste */}
      {!showList ? null : documents?.length > 0 ? (
        <ul className="space-y-1">
          {documents?.map((doc) => (
            <li key={doc?.id} className="group relative">
              <button
                type="button"
                onClick={() => onOpen?.(doc)}
                className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {fileTypeIcon(doc?.file_name, doc?.mime_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate group-hover:text-primary transition-colors">
                    {doc?.file_name || 'document'}
                  </p>
                  {doc?.size_bytes ? (
                    <p className="text-[10px] text-gray-400">{formatFileSize(doc?.size_bytes)}</p>
                  ) : null}
                </div>
              </button>
              {onRemove && (
                <button
                  type="button"
                  onClick={(e) => { e?.stopPropagation(); onRemove(doc); }}
                  title={t('documents.deleteFileAction')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <Icon name="Trash2" size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-400 italic px-1">
          {t('documents.noDocumentsAttached')}
        </p>
      )}

      {/* Progression du lot */}
      {progress && (
        <div className="px-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-medium text-gray-600">
              {t('documents.uploadProgress', { done: progress?.done, total: progress?.total })}
            </span>
            <span className="text-[11px] text-gray-400 tabular-nums">
              {progress?.done}/{progress?.total}
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${Math.round((progress?.done / progress?.total) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Doublons ecartes */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {/* Zone de televersement */}
      {disabled ? (
        disabledHint && <p className="text-xs text-gray-400 italic px-1">{disabledHint}</p>
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            className="hidden"
            onChange={(e) => {
              // On fige la liste AVANT de vider l'input : FileList est une
              // reference vivante que la reinitialisation viderait.
              const files = Array.from(e?.target?.files || []);
              e.target.value = '';
              pick(files);
            }}
          />
          <div
            onClick={() => !busy && inputRef?.current?.click()}
            onDrop={(e) => { e?.preventDefault(); setIsDragOver(false); if (!busy) pick(e?.dataTransfer?.files); }}
            onDragOver={(e) => { e?.preventDefault(); setIsDragOver(true); }}
            onDragEnter={(e) => { e?.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            className={`border-2 border-dashed rounded-xl text-center cursor-pointer transition-all ${
              compact ? 'px-3 py-3' : 'px-4 py-6'
            } ${
              isDragOver
                ? 'border-primary bg-orange-50 scale-[1.01]'
                : 'border-gray-200 hover:border-primary hover:bg-orange-50/30'
            } ${busy ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {busy ? (
              <div className="flex flex-col items-center gap-2">
                <Icon name="Loader2" size={compact ? 16 : 20} className="animate-spin text-primary" />
                <p className="text-xs text-gray-500">{t('documents.uploading')}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1.5">
                <Icon
                  name="Upload"
                  size={compact ? 16 : 20}
                  className={isDragOver ? 'text-primary' : 'text-gray-400'}
                />
                <p className="text-xs font-medium text-gray-600">
                  {t('documents.dropFilesPrefix')}{' '}
                  <span className="text-primary">{t('documents.dropFilesAction')}</span>
                </p>
                <p className="text-[10px] text-gray-400">
                  {hint || t('documents.supportedFormats')}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * DocumentCountButton — icone de colonne standard de l'app :
 * FileText avec une pastille de compte, orange s'il y a des documents.
 * Reprend DocumentIconButton, duplique aujourd'hui dans SubInvoicesPage,
 * ExpensesPage et PaymentRequestsTable.
 */
// `loading` : le compte arrive, petit rond dans la pastille ; le bouton reste
// cliquable (ouvrir la modale n'a pas besoin du compte).
// `count` null : compte indisponible (delai depasse, erreur), pastille « ? ».
export const DocumentCountButton = ({ count = 0, onClick, title, loading = false }) => {
  const unknown = !loading && count == null;
  const has = !loading && count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-busy={loading || undefined}
      className="relative p-1 rounded transition-colors group focus:outline-none focus:ring-2 focus:ring-primary/20"
    >
      <Icon
        name="FileText"
        size={13}
        className={`transition-colors ${has ? 'text-primary' : 'text-gray-400'} group-hover:text-gray-700`}
      />
      <span
        className={`absolute -top-1 -right-1 inline-flex items-center justify-center text-[10px] rounded-full px-1 leading-tight font-medium ${
          has ? 'bg-orange-100 text-primary' : 'bg-gray-200 text-gray-400'
        }`}
        style={{ minWidth: '14px', minHeight: '13px', textAlign: 'center' }}
      >
        {loading ? <Icon name="Loader2" size={8} className="animate-spin text-gray-400" /> : unknown ? '?' : count}
      </span>
    </button>
  );
};

export default DocumentAttach;
